import React, { useEffect, useState } from "react";
import { Box, Stack, Typography, Button } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ThreePIcon from "@mui/icons-material/ThreeP";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// Import SVG icons
import countyIcon from "../images/county.svg";
import districtIcon from "../images/district.svg";
import schoolTypeIcon from "../images/schooltype.svg";
import enrollmentIcon from "../images/enrollment.svg";
import immunizationIcon from "../images/immunization.svg";

// Import data
import { SchoolData, loadVaccinationData } from "../data/dataTable";
import { vaccinationData } from "../data/vaccinationData";
import { mmrData } from "../data/mmrData";
import { exemptionData } from "../data/exemptionData";

interface VaccinationChartProps {
  schoolCode: string;
  onGoBack?: () => void;
}

// Define all possible school years once (2010-11 to 2023-24)
const ALL_SCHOOL_YEARS = [
  "2010-11",
  "2011-12",
  "2012-13",
  "2013-14",
  "2014-15",
  "2015-16",
  "2016-17",
  "2017-18",
  "2018-19",
  "2019-20",
  "2020-21",
  "2021-22",
  "2022-23",
  "2023-24",
];

// Add this custom tooltip formatter
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: "white",
          p: 1,
          border: "1px solid #ccc",
          borderRadius: 1,
        }}
      >
        <Typography variant="body2">{`School Year: ${label}`}</Typography>
        {payload.map((entry: any) =>
          entry.value !== null && entry.value !== undefined ? (
            <Typography
              key={entry.name}
              variant="body2"
              sx={{ color: entry.color }}
            >
              {`${entry.name}: ${entry.value}%`}
            </Typography>
          ) : (
            <Typography
              key={entry.name}
              variant="body2"
              sx={{ color: entry.color }}
            >
              {`${entry.name}: No data available`}
            </Typography>
          )
        )}
      </Box>
    );
  }
  return null;
};

const VaccinationChart: React.FC<VaccinationChartProps> = ({
  schoolCode,
  onGoBack,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [realSchoolCode, setRealSchoolCode] = useState<string | null>(null);
  const [schoolData, setSchoolData] = useState<SchoolData[]>([]);
  const [selectedSchoolData, setSelectedSchoolData] =
    useState<SchoolData | null>(null);
  const [processedMMRData, setProcessedMMRData] = useState<any[]>([]);
  const [processedVaccinationData, setProcessedVaccinationData] = useState<
    any[]
  >([]);
  const [processedExemptionData, setProcessedExemptionData] = useState<any[]>(
    []
  );

  // Load vaccination data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await loadVaccinationData();
        setSchoolData(data);
      } catch (error) {
        console.error("Error loading vaccination data:", error);
        setSchoolData([]);
      }
    };

    fetchData();
  }, []);

  // Find the proper school code for the selected school
  useEffect(() => {
    const findSchoolCode = async () => {
      setIsLoading(true);

      if (!schoolCode || schoolData.length === 0) {
        setRealSchoolCode(null);
        setSelectedSchoolData(null);
        setIsLoading(false);
        return;
      }

      try {
        // Try to find the school data from the loaded data
        const foundSchool = schoolData.find(
          (school) => school.schoolCode === schoolCode
        );

        if (foundSchool) {
          setRealSchoolCode(foundSchool.schoolCode);
          setSelectedSchoolData(foundSchool);
        } else {
          // If no school found, don't use a default
          console.log(`Could not find school code for ${schoolCode}`);
          setRealSchoolCode(null);
          setSelectedSchoolData(null);
        }
      } catch (error) {
        console.error("Error finding school code:", error);
        setRealSchoolCode(null);
        setSelectedSchoolData(null);
      } finally {
        setIsLoading(false);
      }
    };

    findSchoolCode();
  }, [schoolCode, schoolData]);

  // Get the school code for the selected school
  const selectedSchoolCode = realSchoolCode || "";

  // Check if we have data for this school code
  const hasVaccinationData = Boolean(
    vaccinationData[selectedSchoolCode]?.length
  );
  const hasMMRData = Boolean(mmrData[selectedSchoolCode]?.length);
  const hasExemptionData = Boolean(exemptionData[selectedSchoolCode]?.length);

  // Process MMR data to include all years even if data is missing
  useEffect(() => {
    if (!selectedSchoolCode) return;

    // Get raw MMR data for the selected school
    const rawData = mmrData[selectedSchoolCode] || [];

    // Create a map of existing data points
    const dataMap = new Map();
    rawData.forEach((item) => {
      dataMap.set(item.year, item.percentage);
    });

    // Create a complete dataset with all years, using null for missing data
    const processed = ALL_SCHOOL_YEARS.map((year) => ({
      year,
      percentage: dataMap.has(year) ? dataMap.get(year) : null,
    }));

    setProcessedMMRData(processed);
  }, [selectedSchoolCode]);

  // Process vaccination data to include all years even if data is missing
  useEffect(() => {
    if (!selectedSchoolCode) return;

    // Get raw vaccination data for the selected school
    const rawData = vaccinationData[selectedSchoolCode] || [];

    // Create a map of existing data points
    const dataMap = new Map();
    rawData.forEach((item) => {
      dataMap.set(item.year, item.percentage);
    });

    // Create a complete dataset with all years, using null for missing data
    const processed = ALL_SCHOOL_YEARS.map((year) => ({
      year,
      percentage: dataMap.has(year) ? dataMap.get(year) : null,
    }));

    setProcessedVaccinationData(processed);
  }, [selectedSchoolCode]);

  // Process exemption data to include all years even if data is missing
  useEffect(() => {
    if (!selectedSchoolCode) return;

    // Get raw exemption data for the selected school
    const rawData = exemptionData[selectedSchoolCode] || [];

    // Create a map of existing data points
    const dataMap = new Map();
    rawData.forEach((item) => {
      dataMap.set(item.year, {
        personalBeliefExemptions: item.personalBeliefExemptions,
        medicalExemptions: item.medicalExemptions,
      });
    });

    // Create a complete dataset with all years, using null for missing data
    const processed = ALL_SCHOOL_YEARS.map((year) => ({
      year,
      personalBeliefExemptions: dataMap.has(year)
        ? dataMap.get(year).personalBeliefExemptions
        : null,
      medicalExemptions: dataMap.has(year)
        ? dataMap.get(year).medicalExemptions
        : null,
    }));

    setProcessedExemptionData(processed);
  }, [selectedSchoolCode]);

  // If data is loading, show a clear loading message
  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 800,
          margin: "0 auto",
          p: 4,
          mt: 8,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "60vh",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          backgroundColor: "white",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 2,
            fontWeight: "bold",
            color: "#4a90e2",
          }}
        >
          Please wait
        </Typography>
      </Box>
    );
  }

  // If no data available for this school
  if (!hasVaccinationData && !hasMMRData && !hasExemptionData) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 800,
          margin: "0 auto",
          p: 2,
          mt: 5,
          textAlign: "center",
        }}
      >
        <Typography variant="h5">No data available</Typography>
        <Typography variant="body1">
          We couldn't find vaccination trend data for this school:{" "}
          {selectedSchoolCode}
        </Typography>
        <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
          School Code: {selectedSchoolCode}
        </Typography>
      </Box>
    );
  }

  // Helper function to check if chart data is available
  const isDataAvailable = (data: any[]) => {
    return data.some(
      (item) => item.percentage !== null && item.percentage !== undefined
    );
  };

  // Helper function to check if exemption data is available
  const isExemptionDataAvailable = (data: any[]) => {
    return data.some(
      (item) =>
        (item.personalBeliefExemptions !== null &&
          item.personalBeliefExemptions !== undefined) ||
        (item.medicalExemptions !== null &&
          item.medicalExemptions !== undefined)
    );
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: 800,
        margin: "0 auto",
        mt: 5,
      }}
    >
      {/* Back button outside the white box */}
      {onGoBack && (
        <Box sx={{ position: "absolute", right: 0, top: -40, zIndex: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onGoBack}
            size="small"
          >
            Back to DataTable
          </Button>
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          backgroundColor: "white",
          borderRadius: "4px",
          boxShadow:
            "0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)",
          p: 2,
        }}
      >
        {selectedSchoolData && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              gutterBottom
              align="center"
              sx={{ fontWeight: "bold", mb: 0 }}
            >
              {selectedSchoolData.schoolName}
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mb: 0 }}
              justifyContent="center"
              alignItems="center"
            >
              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "#4a90e2",
                  fontStyle: "italic",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <img
                  src={countyIcon}
                  alt="County icon"
                  style={{ width: 20, height: 20 }}
                />
                {selectedSchoolData.county} County
              </Typography>
              {selectedSchoolData.schoolType !== "Private" && (
                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    color: "#4a90e2",
                    fontStyle: "italic",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <img
                    src={districtIcon}
                    alt="District icon"
                    style={{ width: 20, height: 20 }}
                  />
                  District: {selectedSchoolData.district}
                </Typography>
              )}
              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "#4a90e2",
                  fontStyle: "italic",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <img
                  src={schoolTypeIcon}
                  alt="School type icon"
                  style={{ width: 20, height: 20 }}
                />
                {selectedSchoolData.schoolType} School
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  color: "#4a90e2",
                  fontStyle: "italic",
                  mt: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
                align="center"
              >
                <img
                  src={enrollmentIcon}
                  alt="Enrollment icon"
                  style={{ width: 20, height: 20 }}
                />
                Kindergartners(2023-24): {selectedSchoolData.kindergartners}
              </Typography>
            </Stack>
          </Box>
        )}

        <Box sx={{ width: "100%", height: 400, position: "relative" }}>
          <Typography
            variant="h6"
            align="center"
            sx={{ mb: 0, fontWeight: "bold" }}
          >
            Percent of fully vaccinated kindergartners
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: "#6f7378", fontStyle: "italic", mt: 0 }}
            align="center"
          >
            School year: 2010-11 to 2023-24
          </Typography>
          <Box
            sx={{
              width: "70%",
              margin: "0 auto",
              height: "80%",
              position: "relative",
            }}
          >
            {!isDataAvailable(processedVaccinationData) && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.8)",
                  zIndex: 10,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "#666" }}
                >
                  No data available
                </Typography>
              </Box>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={processedVaccinationData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid
                  vertical={false}
                  horizontal={true}
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="year"
                  allowDuplicatedCategory={false}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 20, 40, 60, 80, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={CustomTooltip} />
                <Line
                  name="Vaccination Rate"
                  type="monotone"
                  dataKey="percentage"
                  stroke="#2196f3"
                  dot={{ fill: "#2196f3", r: 4 }}
                  strokeWidth={2}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        {/* Add MMR vaccination chart */}
        <Box sx={{ width: "100%", height: 400, mt: 4, position: "relative" }}>
          <Typography
            variant="h6"
            align="center"
            sx={{ mb: 0, fontWeight: "bold" }}
          >
            MMR (Measles, Mumps, and Rubella) Vaccination Rate
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: "#6f7378", fontStyle: "italic", mt: 0 }}
            align="center"
          >
            School year: 2010-11 to 2023-24
          </Typography>
          <Box
            sx={{
              width: "70%",
              margin: "0 auto",
              height: "80%",
              position: "relative",
            }}
          >
            {!isDataAvailable(processedMMRData) && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.8)",
                  zIndex: 10,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "#666" }}
                >
                  No data available
                </Typography>
              </Box>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={processedMMRData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid
                  vertical={false}
                  horizontal={true}
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="year"
                  allowDuplicatedCategory={false}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 20, 40, 60, 80, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={CustomTooltip} />
                <Line
                  name="MMR Vaccination Rate"
                  type="monotone"
                  dataKey="percentage"
                  stroke="#8884d8"
                  dot={{ fill: "#8884d8", r: 4 }}
                  activeDot={{ r: 6, fill: "#8884d8" }}
                  strokeWidth={2}
                  connectNulls={false}
                  isAnimationActive={true}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        <Box
          sx={{ width: "100%", height: "auto", mt: 4, position: "relative" }}
        >
          <Typography
            variant="h6"
            align="center"
            sx={{ mb: 0, fontWeight: "bold" }}
          >
            Percent of kindergartners with an exemption
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: "#4a90e2", fontStyle: "italic", mt: 0 }}
            align="center"
          >
            School year: 2010-11 to 2023-24
          </Typography>
          <Box sx={{ mb: 2, width: "70%", margin: "0 auto" }}>
            <Typography
              variant="body1"
              sx={{
                color: "#ff9800",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
              align="left"
            >
              <ThreePIcon
                sx={{
                  width: 25,
                  height: 25,
                  color: "#ff9800",
                }}
              />
              Personal Belief Exemption:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }} align="left">
              Effective July 1, 2016, parents can no longer opt out of
              vaccinating children based on personal beliefs.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#33ae80",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
              align="left"
            >
              <MedicalServicesIcon
                sx={{
                  width: 25,
                  height: 25,
                  color: "#33ae80",
                }}
              />
              Medical Exemption:
            </Typography>
            <Typography variant="body2" align="left">
              The 2015 law allows parents to file for medical exemptions under a
              wide range of circumstances.
            </Typography>
          </Box>
          <Box
            sx={{
              width: "70%",
              margin: "0 auto",
              height: 300,
              position: "relative",
            }}
          >
            {!isExemptionDataAvailable(processedExemptionData) && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.8)",
                  zIndex: 10,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "#666" }}
                >
                  No data available
                </Typography>
              </Box>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={processedExemptionData}
                margin={{ top: 0, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid
                  vertical={false}
                  horizontal={true}
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="year"
                  allowDuplicatedCategory={false}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  domain={[0, 20]}
                  ticks={[0, 5, 10, 15, 20]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={CustomTooltip} />
                <Legend
                  verticalAlign="top"
                  align="right"
                  wrapperStyle={{ paddingTop: "0" }}
                />
                <Line
                  name="Personal Belief Exemptions"
                  type="monotone"
                  dataKey="personalBeliefExemptions"
                  stroke="#ff9800"
                  dot={{ fill: "#ff9800" }}
                  strokeWidth={2}
                  connectNulls={false}
                />
                <Line
                  name="Medical Exemptions"
                  type="monotone"
                  dataKey="medicalExemptions"
                  stroke="#33ae80"
                  dot={{ fill: "#33ae80" }}
                  strokeWidth={2}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        <Box sx={{ width: "100%", height: "auto", mt: 2 }}>
          <Box sx={{ mb: 4, width: "70%", margin: "0 auto" }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: 2,
                color: "#33ae80",
              }}
              align="left"
            >
              <Box
                component="img"
                src={immunizationIcon}
                alt="Immunization icon"
                sx={{
                  width: 80,
                  height: 80,
                  display: "flex",
                  alignItems: "center",
                }}
              />
              <Box component="span" sx={{ mt: 2 }}>
                Required Vaccinations:
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }} align="left">
              In general, children can't be admitted unless they obtain the
              required immunizations. (Some students can be conditionally
              admitted even without all the vaccinations.)
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }} align="left">
              • Diphtheria, Tetanus, and Pertussis (DTaP, DTP, Tdap, or Td)
              <br />
              • Polio (OPV or IPV)
              <br />
              • Hepatitis B<br />
              • Measles, Mumps, and Rubella (MMR)
              <br />• Varicella (Chickenpox)
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color: "#4a90e2",
                fontStyle: "italic",
              }}
              align="left"
            >
              Source: California Department of Public Health.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VaccinationChart;
